import React, { useState } from 'react';
import '../../styles/ModuleDetails.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { Settings, BarChart3, Smartphone, Factory } from 'lucide-react';
import ImageModal from '../ImageModal';

const ModuleDetails: React.FC = () => {
  const { t } = useLanguage();
  const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null);

  const openModal = (src: string, alt: string) => {
    setModalImage({ src, alt });
  };

  const closeModal = () => {
    setModalImage(null);
  };

  return (
    <div className="module-details">
      {/* Management Center Module */}
      <section id="management-center" className="module-section">
        <div className="container">
          <div className="module-content">
            <div className="module-info">
              <div className="module-header">
                <div className="module-icon">
                  <Settings className="w-12 h-12" />
                </div>
                <h2>{t('features.module1.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module1.description')}
                </p>
              </div>
              
              <div className="module-features">
                <h3>{t('modules.management.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.management.features.operators')}</li>
                  <li>{t('modules.management.features.shifts')}</li>
                  <li>{t('modules.management.features.fleets')}</li>
                  <li>{t('modules.management.features.activities')}</li>
                  <li>{t('modules.management.features.maintenance')}</li>
                  <li>{t('modules.management.features.operations')}</li>
                </ul>
              </div>
            </div>
            
            <div className="module-image">
              <img 
                src="/images/management_center_page.png" 
                alt="Mozmina Management Center"
                className="module-screenshot clickable-image"
                onClick={() => openModal("/images/management_center_page.png", "Mozmina Management Center")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openModal("/images/management_center_page.png", "Mozmina Management Center");
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Status and Report Module */}
      <section id="status-report" className="module-section">
        <div className="container">
          <div className="module-content reverse">
            <div className="module-image">
              <img 
                src="/images/status_page.png" 
                alt="Mozmina Status and Report"
                className="module-screenshot clickable-image"
                onClick={() => openModal("/images/status_page.png", "Mozmina Status and Report")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openModal("/images/status_page.png", "Mozmina Status and Report");
                  }
                }}
              />
            </div>
            
            <div className="module-info">
              <div className="module-header">
                <div className="module-icon">
                  <BarChart3 className="w-12 h-12" />
                </div>
                <h2>{t('features.module2.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module2.description')}
                </p>
              </div>
              
              <div className="module-features">
                <h3>{t('modules.status.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.status.features.equipment')}</li>
                  <li>{t('modules.status.features.availability')}</li>
                  <li>{t('modules.status.features.utilization')}</li>
                  <li>{t('modules.status.features.mtbf')}</li>
                  <li>{t('modules.status.features.worked')}</li>
                  <li>{t('modules.status.features.operational')}</li>
                  <li>{t('modules.status.features.maintenance')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Module */}
      <section id="mobile" className="module-section">
        <div className="container">
          <div className="module-content">
            <div className="module-info">
              <div className="module-header">
                <div className="module-icon">
                  <Smartphone className="w-12 h-12" />
                </div>
                <h2>{t('features.module3.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module3.description')}
                </p>
              </div>
              
              <div className="module-features">
                <h3>{t('modules.mobile.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.mobile.features.integrated')}</li>
                  <li>{t('modules.mobile.features.interaction')}</li>
                  <li>{t('modules.mobile.features.realtime')}</li>
                  <li>{t('modules.mobile.features.offline')}</li>
                  <li>{t('modules.mobile.features.gps')}</li>
                  <li>{t('modules.mobile.features.intuitive')}</li>
                </ul>
              </div>
            </div>
            
            <div className="module-image">
              <img 
                src="/images/mobile_image.png" 
                alt="Mozmina Mobile"
                className="module-screenshot clickable-image"
                onClick={() => openModal("/images/mobile_image.png", "Mozmina Mobile")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openModal("/images/mobile_image.png", "Mozmina Mobile");
                  }
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Production Module */}
      <section id="production" className="module-section">
        <div className="container">
          <div className="module-content reverse">
            <div className="module-image">
              <img 
                src="/images/production_page.png" 
                alt="Mozmina Production"
                className="module-screenshot clickable-image"
                onClick={() => openModal("/images/production_page.png", "Mozmina Production")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    openModal("/images/production_page.png", "Mozmina Production");
                  }
                }}
              />
            </div>
            
            <div className="module-info">
              <div className="module-header">
                <div className="module-icon">
                  <Factory className="w-12 h-12" />
                </div>
                <h2>{t('features.module4.title')}</h2>
                <p className="module-subtitle">
                  {t('features.module4.description')}
                </p>
              </div>
              
              <div className="module-features">
                <h3>{t('modules.production.features.title')}</h3>
                <ul className="feature-list">
                  <li>{t('modules.production.features.cycle')}</li>
                  <li>{t('modules.production.features.assignment')}</li>
                  <li>{t('modules.production.features.kpis')}</li>
                  <li>{t('modules.production.features.queue')}</li>
                  <li>{t('modules.production.features.tracking')}</li>
                  <li>{t('modules.production.features.realtime')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Production Interfaces Section - Full Width */}
          <div className="production-interfaces-section">
            <div className="production-gallery">
              <h4>{t('modules.production.gallery.title')}</h4>
              
              {/* Production Flow Explanation */}
              <div className="production-flow-section">
                <h5>{t('modules.production.flow.title')}</h5>
                <div className="flow-states">
                  <div className="flow-state">
                    <span className="state-badge state-empty">{t('modules.production.flow.empty')}</span>
                    <p>{t('modules.production.flow.empty.desc')}</p>
                  </div>
                  <div className="flow-arrow">→</div>
                  <div className="flow-state">
                    <span className="state-badge state-loading">{t('modules.production.flow.loading')}</span>
                    <p>{t('modules.production.flow.loading.desc')}</p>
                  </div>
                  <div className="flow-arrow">→</div>
                  <div className="flow-state">
                    <span className="state-badge state-full">{t('modules.production.flow.full')}</span>
                    <p>{t('modules.production.flow.full.desc')}</p>
                  </div>
                  <div className="flow-arrow">→</div>
                  <div className="flow-state">
                    <span className="state-badge state-dumping">{t('modules.production.flow.dumping')}</span>
                    <p>{t('modules.production.flow.dumping.desc')}</p>
                  </div>
                </div>
              </div>

              <div className="production-interfaces">
                <div className="interface-item">
                  <img 
                    src="/images/production_excavator_action.png" 
                    alt="Excavator Actions" 
                    className="clickable-image"
                    onClick={() => openModal("/images/production_excavator_action.png", "Excavator Actions")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        openModal("/images/production_excavator_action.png", "Excavator Actions");
                      }
                    }}
                  />
                  <div className="interface-description">
                    <h6>{t('modules.production.excavator.title')}</h6>
                    <p>{t('modules.production.excavator.desc')}</p>
                  </div>
                </div>

                <div className="interface-item">
                  <img 
                    src="/images/production_truck_action.png" 
                    alt="Truck Actions" 
                    className="clickable-image"
                    onClick={() => openModal("/images/production_truck_action.png", "Truck Actions")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        openModal("/images/production_truck_action.png", "Truck Actions");
                      }
                    }}
                  />
                  <div className="interface-description">
                    <h6>{t('modules.production.truck.title')}</h6>
                    <p>{t('modules.production.truck.desc')}</p>
                  </div>
                </div>

                {/* KPI Monitoring Section */}
                <div className="kpi-monitoring-section">
                  <h5>{t('modules.production.kpi.title')}</h5>
                  <p className="kpi-emphasis">{t('modules.production.kpi.emphasis')}</p>
                  
                  <div className="kpi-categories">
                    <div className="kpi-category">
                      <div className="kpi-header">
                        <span className="kpi-icon">🚛</span>
                        <h6>{t('modules.production.kpi.truck.title')}</h6>
                      </div>
                      <div className="kpi-items">
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.empty.travel')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.empty.travel.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.full.travel')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.full.travel.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.empty.distance')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.empty.distance.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.full.distance')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.full.distance.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.cycle.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.cycle.time.desc')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="kpi-category">
                      <div className="kpi-header">
                        <span className="kpi-icon">⏱️</span>
                        <h6>{t('modules.production.kpi.operations.title')}</h6>
                      </div>
                      <div className="kpi-items">
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.spot.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.spot.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.queue.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.queue.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.loading.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.loading.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.dumping.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.dumping.time.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.hang.time')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.hang.time.desc')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="kpi-category">
                      <div className="kpi-header">
                        <span className="kpi-icon">📊</span>
                        <h6>{t('modules.production.kpi.analytics.title')}</h6>
                      </div>
                      <div className="kpi-items">
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.total.production')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.total.production.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.equipment.utilization')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.equipment.utilization.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.cycle.efficiency')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.cycle.efficiency.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.fleet.performance')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.fleet.performance.desc')}</span>
                        </div>
                        <div className="kpi-item">
                          <span className="kpi-name">{t('modules.production.kpi.realtime.monitoring')}</span>
                          <span className="kpi-desc">{t('modules.production.kpi.realtime.monitoring.desc')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      <ImageModal
        isOpen={modalImage !== null}
        imageSrc={modalImage?.src || ''}
        imageAlt={modalImage?.alt || ''}
        onClose={closeModal}
      />
    </div>
  );
};

export default ModuleDetails;